/**
 * MaidlyDB — IndexedDB schema for offline-first data + sync queue.
 *
 * Three responsibilities:
 *   1. Mirror of `maidly_*` localStorage arrays — gives us large-payload
 *      durability (localStorage caps ~5MB; IndexedDB handles 50MB+).
 *   2. Durable sync queue — actions taken while offline survive page reload.
 *      Each item has an idempotency key so retries don't double-write.
 *   3. Audit log cache so the Audit page works offline.
 *
 * localStorage is still the L1 source-of-truth for synchronous reads by
 * components (we keep AutoSync.js intact). IndexedDB is L2: durable,
 * async, used by the queue + bulk mirror for large datasets and offline
 * resumability.
 */
import Dexie from 'dexie';

class MaidlyDB extends Dexie {
  constructor() {
    super('maidly_db');
    this.version(1).stores({
      // Generic mirror — keyed by `${dataType}::${id}`
      records: '&pk, dataType, id, updated_at',
      // Pending writes when offline OR retrying
      // idempotencyKey is UNIQUE so the same action can't be queued twice
      syncQueue: '++seq, &idempotencyKey, status, dataType, recordId, queuedAt',
      // Audit log cache
      auditLogs: '&id, data_type, record_id, at',
      // Misc kv
      meta: '&key',
    });
  }
}

const db = new MaidlyDB();

// -------- Records mirror --------
async function recordsPut(dataType, items) {
  if (!Array.isArray(items) || items.length === 0) return;
  const rows = items
    .filter((it) => it && it.id)
    .map((it) => ({
      pk: `${dataType}::${it.id}`,
      dataType,
      id: it.id,
      updated_at: it.updated_at || it._last_updated_at || new Date().toISOString(),
      data: it,
    }));
  await db.records.bulkPut(rows);
}

async function recordsRemove(dataType, ids) {
  if (!Array.isArray(ids) || ids.length === 0) return;
  await db.records.bulkDelete(ids.map((id) => `${dataType}::${id}`));
}

async function recordsList(dataType) {
  const rows = await db.records.where({ dataType }).toArray();
  return rows.map((r) => r.data);
}

// -------- Sync queue --------
const STATUS = { pending: 'pending', inflight: 'inflight', failed: 'failed' };

async function queueEnqueue(action) {
  // action: { idempotencyKey, op: 'save'|'delete', dataType, recordId, payload }
  const row = {
    ...action,
    status: STATUS.pending,
    attempts: 0,
    queuedAt: new Date().toISOString(),
    lastError: null,
  };
  try {
    await db.syncQueue.add(row);
  } catch (e) {
    // Unique-constraint collision on idempotencyKey — already queued, skip
    if (e?.name !== 'ConstraintError') throw e;
  }
}

async function queuePeek(limit = 25) {
  return db.syncQueue
    .where('status')
    .anyOf(STATUS.pending, STATUS.failed)
    .limit(limit)
    .toArray();
}

async function queueMarkInflight(seq) {
  await db.syncQueue.update(seq, { status: STATUS.inflight });
}

async function queueAck(seq) {
  await db.syncQueue.delete(seq);
}

async function queueFail(seq, errorMsg) {
  const row = await db.syncQueue.get(seq);
  if (!row) return;
  await db.syncQueue.update(seq, {
    status: STATUS.failed,
    attempts: (row.attempts || 0) + 1,
    lastError: String(errorMsg || '').slice(0, 500),
  });
}

async function queueCount() {
  return db.syncQueue.count();
}

// -------- Audit log cache --------
async function auditPut(logs) {
  if (!Array.isArray(logs) || logs.length === 0) return;
  await db.auditLogs.bulkPut(logs);
}

async function auditList({ limit = 200 } = {}) {
  return db.auditLogs.orderBy('at').reverse().limit(limit).toArray();
}

// -------- Meta kv --------
async function metaGet(key) {
  const row = await db.meta.get(key);
  return row?.value ?? null;
}
async function metaSet(key, value) {
  await db.meta.put({ key, value });
}

export default {
  db,
  records: { put: recordsPut, remove: recordsRemove, list: recordsList },
  queue: {
    enqueue: queueEnqueue,
    peek: queuePeek,
    markInflight: queueMarkInflight,
    ack: queueAck,
    fail: queueFail,
    count: queueCount,
    STATUS,
  },
  audit: { put: auditPut, list: auditList },
  meta: { get: metaGet, set: metaSet },
};
